import { CloseOutlined, PlusOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { Avatar, Button, Col, Divider, Drawer, Flex, Input, Select, Typography } from "antd";
import { DefaultOptionType } from "antd/es/select";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { setAddClientView } from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import styles from "./createnotemodal.module.scss";

const ClientSelector = () => {
  const componentName = "createnotemodal";
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const { isEditNote } = useAppSelector((state) => state.appState);
  const { clients } = useAppSelector((state) => state.client);
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const { referIndividualBy } = useSpecialityReference();

  // Mobile drawer state
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");

  const clientOptions = useMemo(() => {
    return clients.map((client) => {
      const formattedDob = client.date_of_birth ? dayjs(client.date_of_birth).format("MM/DD/YYYY") : "";
      const derivedAgeFromDob =
        client.date_of_birth && formattedDob ? dayjs().diff(dayjs(client.date_of_birth), "year") : null;
      const rawAge =
        typeof client.age === "number" ? client.age : client.age ? Number.parseInt(client.age.toString(), 10) : null;
      const displayAge = formattedDob ? derivedAgeFromDob : rawAge;
      const searchTokens = [client.name];

      if (client.mrn) {
        searchTokens.push(client.mrn);
      }
      if (formattedDob) {
        searchTokens.push(formattedDob);
      } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
        searchTokens.push(displayAge.toString());
      }

      const secondaryDetails: string[] = [];
      if (formattedDob) {
        secondaryDetails.push(`DOB: ${formattedDob}`);
      } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
        secondaryDetails.push(`Age: ${displayAge}`);
      }
      if (client.mrn) {
        secondaryDetails.push(`MRN: ${client.mrn}`);
      }

      return {
        label: (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>
              {client.name}{" "}
              {!client.is_owned && client.owner_name && (
                <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                  - Shared by {client.owner_name}
                </span>
              )}
            </span>
            {secondaryDetails.length > 0 && (
              <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                {secondaryDetails.join(" · ")}
              </span>
            )}
          </div>
        ),
        value: client.id,
        optionLabel: client.name,
        searchKey: searchTokens.join(" ").toLowerCase(),
        secondaryDetails,
        isOwned: client.is_owned,
        ownerName: client.owner_name,
      };
    });
  }, [clients]);

  // Filtered clients for mobile drawer
  const filteredMobileClients = useMemo(() => {
    if (!mobileSearchQuery.trim()) return clientOptions;
    const query = mobileSearchQuery.toLowerCase().trim();
    return clientOptions.filter((client) => client.searchKey.includes(query));
  }, [clientOptions, mobileSearchQuery]);

  // Get selected client name for display
  const selectedClientName = useMemo(() => {
    const selected = clients.find((c) => c.id === recordingDetail?.client_id);
    return selected?.name || null;
  }, [clients, recordingDetail?.client_id]);

  const handleMobileClientSelect = useCallback(
    (clientId: number) => {
      dispatch(
        setRecordingDetail({
          ...recordingDetail,
          client_id: clientId,
        }),
      );
      setIsMobileDrawerOpen(false);
      setMobileSearchQuery("");
    },
    [dispatch, recordingDetail],
  );

  const handleAddClientMobile = useCallback(() => {
    setIsMobileDrawerOpen(false);
    setMobileSearchQuery("");
    dispatch(setAddClientView(true));
  }, [dispatch]);

  // Mobile: render a trigger button + bottom drawer
  if (isMobile) {
    return (
      <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
        <Typography.Text>{referIndividualBy}:</Typography.Text>
        {/* Mobile trigger button */}
        <Button
          onClick={() => setIsMobileDrawerOpen(true)}
          className={`${styles[`${componentName}__modal-row-field`]} ${styles[`${componentName}__client-trigger`]}`}
          style={{
            width: "100%",
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            textAlign: "left",
            marginTop: 5,
            padding: "0 12px",
            borderColor: "var(--ant-color-border, #d9d9d9)",
          }}
        >
          <span
            style={{
              color: selectedClientName ? "inherit" : "var(--ant-color-text-placeholder, #bfbfbf)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedClientName || `Select a ${referIndividualBy.toLowerCase()}`}
          </span>
          <RightOutlined style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }} />
        </Button>

        {/* Mobile bottom drawer */}
        <Drawer
          title={`Select ${referIndividualBy}`}
          placement="bottom"
          height="85%"
          open={isMobileDrawerOpen}
          onClose={() => {
            setIsMobileDrawerOpen(false);
            setMobileSearchQuery("");
          }}
          styles={{
            body: {
              padding: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          }}
          closeIcon={<CloseOutlined />}
        >
          {/* Sticky search header */}
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--ant-color-border-secondary, #f0f0f0)",
              backgroundColor: "var(--ant-color-bg-container, #fff)",
            }}
          >
            <Input
              prefix={<SearchOutlined style={{ color: "var(--ant-color-text-tertiary, #8c8c8c)" }} />}
              placeholder={`Search by name, DOB, or MRN...`}
              value={mobileSearchQuery}
              onChange={(e) => setMobileSearchQuery(e.target.value)}
              allowClear
              size="large"
              style={{ borderRadius: 8 }}
              autoFocus
            />
          </div>

          {/* Scrollable client list */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {filteredMobileClients.length === 0 ? (
              <div
                style={{
                  padding: "40px 16px",
                  textAlign: "center",
                  color: "var(--ant-color-text-tertiary, #8c8c8c)",
                }}
              >
                <Typography.Text type="secondary">No {referIndividualBy.toLowerCase()}s found</Typography.Text>
              </div>
            ) : (
              filteredMobileClients.map((client) => {
                const clientName = client.optionLabel || "";
                const initials = clientName
                  .split(" ")
                  .filter((word: string) => word.length > 0)
                  .map((word: string) => word.charAt(0).toUpperCase())
                  .slice(0, 2)
                  .join("");
                const isSelected = recordingDetail?.client_id === client.value;

                return (
                  <div
                    key={client.value}
                    onClick={() => handleMobileClientSelect(client.value as number)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "8px",
                      borderBottom: "1px solid var(--ant-color-border-secondary, #f0f0f0)",
                      backgroundColor: isSelected
                        ? "var(--ant-color-primary-bg, #e6f4ff)"
                        : "var(--ant-color-bg-container, #fff)",
                      cursor: "pointer",
                      minHeight: 48,
                    }}
                  >
                    <Avatar
                      style={{
                        backgroundColor: getRandomAvatarColor(clientName),
                        flexShrink: 0,
                      }}
                      size={40}
                    >
                      {initials}
                    </Avatar>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: isSelected ? 600 : 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {clientName}
                        {!client.isOwned && client.ownerName && (
                          <span
                            style={{
                              fontSize: 12,
                              color: "var(--ant-color-text-tertiary, #8c8c8c)",
                              marginLeft: 6,
                            }}
                          >
                            - Shared by {client.ownerName}
                          </span>
                        )}
                      </div>
                      {client.secondaryDetails.length > 0 && (
                        <div
                          style={{
                            fontSize: 13,
                            color: "var(--ant-color-text-tertiary, #8c8c8c)",
                            marginTop: 2,
                          }}
                        >
                          {client.secondaryDetails.join(" · ")}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: "var(--ant-color-primary, #1677ff)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>✓</span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Sticky footer with Add button */}
          {!isEditNote && (
            <div
              style={{
                padding: "12px 16px",
                borderTop: "1px solid var(--ant-color-border-secondary, #f0f0f0)",
                backgroundColor: "var(--ant-color-bg-container, #fff)",
              }}
            >
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddClientMobile}
                block
                size="large"
                style={{ borderRadius: 8 }}
              >
                Add New {referIndividualBy}
              </Button>
            </div>
          )}
        </Drawer>
      </Col>
    );
  }

  // Desktop: keep the original Select dropdown
  return (
    <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
      <Typography.Text>{referIndividualBy}:</Typography.Text>
      <Select
        value={recordingDetail?.client_id}
        listHeight={256}
        onChange={(value) => {
          dispatch(
            setRecordingDetail({
              ...recordingDetail,
              client_id: value,
            }),
          );
        }}
        showSearch
        placeholder={`Select a ${referIndividualBy.toLowerCase()}`}
        optionFilterProp="children"
        filterOption={(input, option) => {
          const normalizedInput = input.trim().toLowerCase();
          if (!normalizedInput) {
            return true;
          }
          if (!option) {
            return false;
          }
          const optionWithSearchKey = option as DefaultOptionType & { searchKey?: string };
          if (optionWithSearchKey.searchKey?.includes(normalizedInput)) {
            return true;
          }
          const { label } = optionWithSearchKey;
          const labelText = typeof label === "string" ? label : typeof label === "number" ? String(label) : "";
          if (labelText) {
            return labelText.toLowerCase().includes(normalizedInput);
          }
          return false;
        }}
        labelRender={() => {
          const selectedClient = clients.find((c) => c.id === recordingDetail?.client_id);
          if (!selectedClient) return null;

          return selectedClient.name;
        }}
        optionRender={(client) => {
          const clientName = client.data?.optionLabel || "";
          const initials = clientName
            .split(" ")
            .filter((word) => word.length > 0)
            .map((word) => word.charAt(0).toUpperCase())
            .slice(0, 2)
            .join("");

          return (
            <Flex gap={10} align="center">
              <Avatar
                style={{
                  backgroundColor: getRandomAvatarColor(clientName),
                }}
                size="small"
              >
                {initials}
              </Avatar>
              <Typography.Text>{client.label}</Typography.Text>
            </Flex>
          );
        }}
        dropdownRender={(menu) => (
          <div className="client">
            {menu}
            {!isEditNote ? (
              <>
                <Divider style={{ margin: "4px 0" }} />
                <Flex gap={10}>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      dispatch(setAddClientView(true));
                    }}
                  >
                    Add {referIndividualBy}
                  </Button>
                </Flex>
              </>
            ) : null}
          </div>
        )}
        className={`${styles[`${componentName}__modal-row-field`]}`}
        options={clientOptions}
      />
    </Col>
  );
};

export default ClientSelector;
